import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  AssignmentPlan,
  PlannerSlot,
  slotDay,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import {
  calendarSlotIsUsable,
  plannerCalendarRows,
} from "@/components/appointmentRequest/assignmentPlanner/plannerCalendarRows";
import { formatAppointmentRequestAvailableDaysText } from "@/helper/appointmentRequestAvailableDays";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

interface Props {
  slots: PlannerSlot[];
  plan: AssignmentPlan;
  day: string;
  onDay: (day: string) => void;
  initialScrollDay?: string;
}
export default function PlannerCalendar({
  slots,
  plan,
  day,
  onDay,
  initialScrollDay,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("de") ? "de-DE" : "en-GB";
  const today = dayjs().format("YYYY-MM-DD");
  const rows = plannerCalendarRows(slots);
  const months = [...new Set(rows.map((row) => row.month))].map((month) => ({
    month,
    weeks: rows.filter((row) => row.month === month),
  }));
  const monday = dayjs(day).subtract((dayjs(day).day() + 6) % 7, "day");
  const layout = rows.map((row) => row.key).join(",");
  const viewport = useRef<HTMLDivElement>(null);
  const initializedLayout = useRef<string>();
  const assigned = new Set(Object.values(plan));
  const byDay = new Map<string, PlannerSlot[]>();
  for (const slot of slots) {
    const key = slotDay(slot);
    const entries = byDay.get(key);
    if (entries) entries.push(slot);
    else byDay.set(key, [slot]);
  }
  const days = [...byDay.keys()]
    .filter((key) => byDay.get(key)!.some(calendarSlotIsUsable))
    .sort();
  useEffect(() => {
    const node = viewport.current;
    if (!node) return;
    const initial = initializedLayout.current !== layout;
    initializedLayout.current = layout;
    if (initial && !initialScrollDay) {
      node.scrollTop = 0;
      return;
    }
    const target = initial ? initialScrollDay : day;
    const cell = node.querySelector<HTMLElement>(
      `[data-calendar-day="${target}"]`,
    );
    if (!cell) return;
    const visible = node.getBoundingClientRect();
    const row = cell.closest<HTMLElement>("[data-calendar-week]") ?? cell;
    const bounds = row.getBoundingClientRect();
    if (bounds.top < visible.top || bounds.bottom > visible.bottom)
      node.scrollTop += bounds.top - visible.top;
  }, [day, layout, initialScrollDay]);
  return (
    <section aria-label={t("planner.calendar")} className={styles.calendar}>
      <div className={styles.toolbar}>
        <strong>
          {formatAppointmentRequestAvailableDaysText({
            available_day_set: days,
          }) ?? t("planner.no_bookable_dates")}
        </strong>
      </div>
      {rows.length > 0 && (
        <div className={styles.calendarWeekdays} aria-hidden="true">
          <span />
          <div className={styles.calendarWeekdayNames}>
            {Array.from({ length: 7 }, (_, index) => (
              <span key={index}>
                {new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
                  monday.add(index, "day").toDate(),
                )}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className={styles.calendarScrollArea}>
        <div
          className={styles.calendarRows}
          role="group"
          aria-label={t("planner.calendar_weeks")}
          tabIndex={rows.length ? 0 : undefined}
          ref={viewport}
        >
          {months.map(({ month, weeks }) => (
            <div
              className={styles.calendarMonthGroup}
              key={month}
              data-calendar-month={month}
            >
              <span className={styles.calendarMonth}>
                {new Intl.DateTimeFormat(locale, {
                  month: "long",
                  year: "numeric",
                }).format(dayjs(`${month}-01`).toDate())}
              </span>
              <div className={styles.calendarMonthWeeks}>
                {weeks.map((row) => (
                  <div
                    className={styles.calendarWeek}
                    key={row.key}
                    data-calendar-week={row.key}
                  >
                    {row.days.map((key, column) => {
                      if (!key) return <span key={column} />;
                      const entries = byDay.get(key) ?? [];
                      const usable = entries.filter(calendarSlotIsUsable);
                      const fullyAssigned =
                        usable.length > 0 &&
                        usable.every(
                          (slot) =>
                            assigned.has(slot.id) ||
                            (slot.occupied && !slot.employee_id),
                        );
                      return (
                        <button
                          type="button"
                          key={key}
                          data-calendar-day={key}
                          data-weekend={column >= 5 || undefined}
                          className={
                            fullyAssigned ? styles.assigned : undefined
                          }
                          aria-label={dayjs(key).format("DD.MM.YYYY")}
                          aria-current={key === today ? "date" : undefined}
                          aria-description={
                            fullyAssigned
                              ? t("planner.day_fully_assigned")
                              : undefined
                          }
                          aria-pressed={day === key}
                          disabled={!usable.length}
                          onClick={() => onDay(key)}
                        >
                          <strong>{dayjs(key).format("D")}</strong>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
